import { axiosClientCore, axiosClientRegisters } from "@duosoftbg/nacid-frontoffice-components";
import { ApiEndpoints } from "./endpoints";
import qs from "qs";
import { ReferenceDataDomain } from "@duosoftbg/nacid-components";
import { removeEmptyFieldsFromObject } from "../../utils/utils";

const commonFilterFunction = async (url, params) => {
  const response = await axiosClientRegisters.get(url, {
    params,
    paramsSerializer: (params) => {
      return qs.stringify(params);
    },
  });

  return response.data;
};

export const getAllAcademicRecognitionNacidStatuses = () => async () => {
  const response = await axiosClientCore.get(ApiEndpoints.nomenclature.referenceData.base, {
    params: {
      domain: ReferenceDataDomain.ACADEMIC_RECOGNITION_NACID_STATUS,
    },
  });
  return response.data;
};

export const getAllAcademicRecognitionUniStatuses = () => async () => {
  const response = await axiosClientCore.get(ApiEndpoints.nomenclature.referenceData.base, {
    params: {
      domain: ReferenceDataDomain.ACADEMIC_RECOGNITION_UNI_STATUS,
    },
  });
  return response.data;
};

export const getAllRegProfStatuses = () => async () => {
  const response = await axiosClientCore.get(ApiEndpoints.nomenclature.referenceData.base, {
    params: {
      domain: ReferenceDataDomain.REGPROF_STATUS,
    },
  });
  return response.data;
};

export const existsSecondaryByAccessCode = (accessCode: string) => async (): Promise<boolean> => {
  const response = await axiosClientRegisters.get(
    ApiEndpoints.secondary.existsByAccessCode.replace("{accessCode}", accessCode),
  );
  return response.data;
};

export const getSecondaryByAccessCode = (accessCode: string) => async () => {
  const response = await axiosClientRegisters.get(
    ApiEndpoints.secondary.findByAccessCode.replace("{accessCode}", accessCode),
  );
  return response.data;
};

export const getSecondaryByQrCodeUUID = (qrCodeUUID: string) => async () => {
  const response = await axiosClientRegisters.get(
    ApiEndpoints.secondary.findByQrCodeUUID.replace("{accessCode}", qrCodeUUID),
  );
  return response.data;
};

export const searchAcademicRecognitionNacid = (params) => async () => {
  return commonFilterFunction(
    ApiEndpoints.academicRecognition.nacid.filterRecords,
    removeEmptyFieldsFromObject(params),
  );
};

export const searchAcademicRecognitionUniversities = (params) => async () => {
  return commonFilterFunction(
    ApiEndpoints.academicRecognition.universities.filterRecords,
    removeEmptyFieldsFromObject(params),
  );
};

export const searchProfessionalQualifications = (params) => async () => {
  return commonFilterFunction(
    ApiEndpoints.professionalQualification.filterRecords,
    removeEmptyFieldsFromObject(params),
  );
};

export const getAllCountries = () => async () => {
  const response = await axiosClientCore.get(`${ApiEndpoints.nomenclature.countries}`);
  return response.data;
};

export const getAllEduLevels = () => async () => {
  const response = await axiosClientCore.get(ApiEndpoints.nomenclature.referenceData.base, {
    params: {
      domain: ReferenceDataDomain.EDUCATION_LEVEL,
    },
  });
  return response.data;
};

export const getAllARUniEntryRequestStatuses = () => async () => {
  const response = await axiosClientCore.get(ApiEndpoints.nomenclature.referenceData.base, {
    params: {
      domain: ReferenceDataDomain.ACADEMIC_RECOGNITION_UNI_ENTRY_REQUEST_STATUS,
    },
  });
  return response.data;
};

export const getNacidRecognition = (nacidIdentifier) => async () => {
  const response = await axiosClientRegisters.get(ApiEndpoints.academicRecognition.nacid.arNacidRecord, {
    params: {
      id: nacidIdentifier,
    },
  });
  return response.data;
};

export const getUniversitiesRecognition =
  (uniIdentifier, withExtraData = false) =>
  async () => {
    const params = {
      withExtraData: withExtraData,
    };

    return commonFilterFunction(
      `${ApiEndpoints.academicRecognition.universities.arUniversitiesRecord}/${uniIdentifier}`,
      params,
    );
  };

export const getProfessionalQualification = (qualifIdentifier) => async () => {
  const response = await axiosClientRegisters.get(ApiEndpoints.professionalQualification.professionalQualification, {
    params: {
      id: qualifIdentifier,
    },
  });
  return response.data;
};

export const getARUniApplicantsAutocomplete = (inputValue) =>
  getStringListResponseAutocomplete(inputValue, ApiEndpoints.academicRecognition.universities.autocompleteApplicants);

export const getARUniUniversitiesAutocomplete = (inputValue) =>
  getStringListResponseAutocomplete(inputValue, ApiEndpoints.academicRecognition.universities.autocompleteUniversities);

export const getARUniDiplomaSpecialitiesAutocomplete = (inputValue) =>
  getStringListResponseAutocomplete(
    inputValue,
    ApiEndpoints.academicRecognition.universities.autocompleteDiplomaSpecialities,
  );

export const getARUniRecognizedSpecialitiesAutocomplete = (inputValue) =>
  getStringListResponseAutocomplete(
    inputValue,
    ApiEndpoints.academicRecognition.universities.autocompleteRecognizedSpecialities,
  );

export const getARUniEduLevelsAutocomplete = (inputValue) =>
  getStringListResponseAutocomplete(
    inputValue,
    ApiEndpoints.academicRecognition.universities.autocompleteEducationLevels,
  );

const getStringListResponseAutocomplete = (inputValue, url) => async () => {
  const response = await axiosClientRegisters.get(url, {
    params: {
      name: inputValue,
    },
  });
  return response.data;
};

export const saveARUniEntryRequest = (formData) => async () => {
  const response = await axiosClientRegisters.post(ApiEndpoints.arUniEntryRequest.saveARUniEntryRequest, formData);
  return response.data;
};

export const saveAndPublishARUniEntryRequest = (formData) => async () => {
  const response = await axiosClientRegisters.post(
    ApiEndpoints.arUniEntryRequest.saveAndPublishARUniEntryRequest,
    formData,
  );
  return response.data;
};

export const getLoggedUserDetails = () => async () => {
  const response = await axiosClientCore.get(`${ApiEndpoints.user.loggedUser}`, {});
  return response.data;
};

export const getAllNationalUniversities = () => async () => {
  const response = await axiosClientCore.get(`${ApiEndpoints.nomenclature.nationalUniversities}`);
  return response.data;
};

export const filterARUniEntryRequests = (filter) => () => {
  return commonFilterFunction(ApiEndpoints.arUniEntryRequest.filterRequests, removeEmptyFieldsFromObject(filter));
};

export const getARUniEntryRequest = (id) => async () => {
  const response = await axiosClientRegisters.get(`${ApiEndpoints.arUniEntryRequest.getRequest}/${id}`);
  return response.data;
};

export const acceptARUniEntryRequest = (id) => async () => {
  const response = await axiosClientRegisters.post(`${ApiEndpoints.arUniEntryRequest.accept}?id=${id}`);
  return response;
};

export const rejectARUniEntryRequest = (id) => async () => {
  await axiosClientRegisters.post(`${ApiEndpoints.arUniEntryRequest.reject}?id=${id}`);
};

export const getARUniEntryRequestAllowedEduLevels = () => async () => {
  const response = await axiosClientRegisters.get(`${ApiEndpoints.arUniEntryRequest.allowedEduLevels}`, {});
  return response.data;
};

export const getARUniEntryRequestAllowedRecognitionStatuses = () => async () => {
  const response = await axiosClientRegisters.get(`${ApiEndpoints.arUniEntryRequest.allowedRecognitionStatuses}`, {});
  return response.data;
};

export const getContentManagementDataById = (id: string) => async () => {
  const response = await axiosClientCore.get(`${ApiEndpoints.nomenclature.contentManagement}/${id}`);
  return response.data;
};

export const autocompleteRelatedRecognitions = (value, page, pageSize) => async () => {
  const response = await axiosClientRegisters.get(ApiEndpoints.academicRecognition.universities.autocompleteRecords, {
    params: {
      name: value,
      page,
      pageSize,
    },
  });
  return response.data;
};

export const getProfGroupOptions = () => async () => {
  const response = await axiosClientCore.get(`${ApiEndpoints.nomenclature.profGroup}`);
  return response;
};

export const getAppIdByCertificateNumber = (type, uuid) => async () => {
  const response = await axiosClientRegisters.get(`${ApiEndpoints.certificate.base}/${type}/${uuid}`, {});
  return response.data;
};
